import { z as baseZod } from 'zod'

import {
  filesDeleteOptions,
  filesGetOptions,
  filesListOptions,
  filesUploadOptions,
} from './files-options'
import { FlowExtensionParamWidgetTypes, type FrWidgetToken } from './widget-types'

// The v2 authoring surface: real zod v4 extended with FlowRunner authoring plugins and custom types.
// Exposed to services as `Flowrunner.z` (services do `const z = Flowrunner.z`) — see ../../namespace.ts.
//
// Author metadata is stored on the schema via zod's native `.meta()` (merges, immutable clone). Native
// keys drive display (`title`, `description`, `examples`); FlowRunner-specific bits use `fr`-prefixed keys
// so they never collide with zod/OpenAPI meta. The runner reads them back via ../schema/introspect.

// A dictionary ref carried by `.dictionary(ref)` — returned from `ext.dictionary(...)` (see create-extension).
export interface DictionaryRef {
  id: string
  dependsOn?: string[]
}

// FlowRunner meta keys attached by the plugins below.
export interface FrMeta {
  title?: string
  description?: string
  examples?: unknown[]
  frShared?: boolean
  frDictionary?: { id: string; dependsOn: string[] }
  frLabels?: Record<string, string>
  /** A widget token the declaration carries explicitly — see `FrWidgetToken` in ./widget-types.ts. */
  frWidget?: FrWidgetToken
  frSecret?: boolean
  /** `z.json()` — the value arrives as TEXT and is parsed before the handler sees it. See `json()` below. */
  frJson?: boolean
}

declare module 'zod' {
  interface ZodType {
    /** Display label → native `meta.title`. */
    label(text: string): this
    /** Sample value → appended to native `meta.examples`. */
    example(value: unknown): this
    /** Mark a config field as shared (global) rather than per-app (OAuth client id/secret, secrets). */
    shared(): this
    /**
     * Mark a `z.string()` CONFIG field as sensitive: the console masks it and offers a reveal toggle.
     *
     * Display only. The value is stored, transmitted and validated exactly as it would be without it — this
     * hides a credential from a passer-by, it does not protect it.
     *
     * No effect anywhere else. Not on `z.text()`, which has no password mode; and not on an action or
     * trigger param, whose value lives in the flow definition and is readable through the API, so masking
     * its input would suggest a protection that is not there.
     */
    secret(): this
    /** Bind a param to a dictionary for dynamic options; auto-derives `dependsOn` from the dictionary criteria. */
    dictionary(ref: DictionaryRef): this
    /**
     * Show a human label per declared VALUE: `.labels({ asc: 'Ascending' })`.
     *
     * The flow element stores the VALUE, so renaming a label changes nothing that was saved — and the
     * handler receives the value it declared, with no translation step anywhere.
     */
    labels(byValue: Record<string, string>): this
    /** @deprecated Inverted and renamed — see `labels`. Throws, so the change cannot pass silently. */
    map(mapping: Record<string, unknown>): never
  }
}

let extended = false

function extendPrototype(): void {
  if (extended) {
    return
  }
  extended = true

  const proto = baseZod.ZodType.prototype as unknown as Record<string, unknown>

  proto.label = function label(this: baseZod.ZodType, text: string) {
    return this.meta({ title: text })
  }

  proto.example = function example(this: baseZod.ZodType, value: unknown) {
    const existing = (this.meta() as FrMeta | undefined)?.examples ?? []

    return this.meta({ examples: [...existing, value] })
  }

  proto.shared = function shared(this: baseZod.ZodType) {
    return this.meta({ frShared: true })
  }

  proto.secret = function secret(this: baseZod.ZodType) {
    return this.meta({ frSecret: true })
  }

  proto.dictionary = function dictionary(this: baseZod.ZodType, ref: DictionaryRef) {
    return this.meta({ frDictionary: { id: ref.id, dependsOn: ref.dependsOn ?? [] } })
  }

  proto.labels = function labels(this: baseZod.ZodType, byValue: Record<string, string>) {
    return this.meta({ frLabels: byValue })
  }

  /**
   * `.map({ Label: value })` declared the LABELS as the accepted values, so a flow element stored a display
   * string — renaming a label then broke every saved element that used it. `.labels()` inverts it: the enum
   * declares the values, the labels are display only.
   *
   * It throws rather than being quietly accepted because the two forms take the same shape of argument with
   * the opposite meaning. Silently reading an old `.map()` would store labels again; ignoring it would drop
   * the labels. A loud failure at registration is the only reading that cannot corrupt a flow.
   */
  proto.map = function map(this: baseZod.ZodType): never {
    throw new Error(
      '`.map({ Label: value })` has been replaced by `.labels({ value: \'Label\' })` — declare the VALUES in ' +
      'the enum and the labels here, so a flow element stores the value and renaming a label is safe. ' +
      'e.g. z.enum([\'asc\', \'desc\']).labels({ asc: \'Ascending\', desc: \'Descending\' })'
    )
  }
}

extendPrototype()

// Custom type: multi-line text is a `z.string()` carrying a widget marker.
function text() {

  return baseZod.string().meta({ frWidget: FlowExtensionParamWidgetTypes.MULTI_LINE_TEXT })
}

/**
 * Custom type: a parameter that takes arbitrary JSON.
 *
 * Three facts have to hold at once, and each picks one piece of this declaration:
 *
 * - **The console renders a textarea.** `frWidget` says so, and ../schema/introspect.ts publishes the
 *   parameter as `STRING` — the model describes what a caller SUPPLIES, and what a caller supplies is text.
 * - **`execute` receives the parsed structure.** `frJson` tells ../schema/coerce.ts to `JSON.parse` the text
 *   on the way in, so no service writes its own parse and none has to invent wording for text that is not
 *   JSON. Coercion already sits between the transport and zod for exactly this kind of conversion — this is
 *   the same arrangement as `z.string().pipe(z.coerce.number())`, with a fixed conversion.
 * - **The author's TYPE is the parsed value, not the text.** Hence `unknown` underneath: `z.infer` gives the
 *   handler `unknown`, which is what it actually receives. Declaring `z.string()` here would publish and
 *   coerce correctly and then lie to every author about what lands in `params`.
 *
 * ⚠️ This SHADOWS zod's own `z.json()` (the namespace is `Object.create(baseZod)`). Deliberate, and safe
 * only because zod's version cannot currently be declared at all — it recurses until the stack is gone, and
 * the extension never registers (FR-3600). An author reaching for `z.json()` gets this one.
 */
function json() {

  // `.nonoptional()` because `z.unknown()` accepts `undefined` on its own, and the console asks the same
  // absence probe the runtime resolves with (see ../model/field-mapper.ts `isRequired`) — so without it a
  // bare `z.json()` would publish as an OPTIONAL field and the runner would accept a missing value, which is
  // not what an author who did not write `.optional()` asked for. An author's own `.optional()` still wins:
  // it lands outside this, and the outermost modifier is the final word.
  return baseZod.unknown().nonoptional().meta({
    frWidget: FlowExtensionParamWidgetTypes.MULTI_LINE_TEXT,
    frJson  : true,
  })
}

// NB: never mutate `baseZod` itself — zod's module exports are not extensible. The ESM build exposes
// `z` as a frozen module namespace, and since zod 4.6.0 the CJS build freezes its exports too, so
// `Object.assign(baseZod, ...)` throws "Cannot add property text, object is not extensible" at module
// load — which takes down the whole extension worker thread before any service code runs. Layering the
// additions onto a child object keeps every zod export reachable through the prototype chain while
// leaving zod's own exports untouched.
// The additions are DEFINED rather than assigned, for a second-order version of the same hazard: `json`
// SHADOWS a zod export, `Object.assign` writes through plain assignment, and assignment respects the
// PROTOTYPE's descriptor — zod's `json` is non-writable, so it throws "Cannot assign to read only property
// 'json'" at module load and takes the worker down exactly as mutating `baseZod` would. `defineProperty`
// creates an own property without consulting the prototype, which is what shadowing requires.
//
// `Omit` on the base is what makes `z.json()` unambiguous: intersecting the two signatures would leave zod's
// recursive-JSON overload callable, and an author would get its type for our value.
const namespace = Object.create(baseZod) as typeof baseZod

// The file-options groups are custom types in the same sense — see ./files-options.ts for why they are
// shipped rather than hand-copied into each service.
Object.defineProperties(namespace, {
  text: { value: text, enumerable: true },
  json: { value: json, enumerable: true },
  filesUploadOptions: { value: filesUploadOptions, enumerable: true },
  filesListOptions: { value: filesListOptions, enumerable: true },
  filesGetOptions: { value: filesGetOptions, enumerable: true },
  filesDeleteOptions: { value: filesDeleteOptions, enumerable: true },
})

export const z = namespace as unknown as
  Omit<typeof baseZod, 'json'> & {
    text: typeof text
    json: typeof json
    filesUploadOptions: typeof filesUploadOptions
    filesListOptions: typeof filesListOptions
    filesGetOptions: typeof filesGetOptions
    filesDeleteOptions: typeof filesDeleteOptions
  }

export type ExtendedZod = typeof z
